/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Filter, Search, BarChart2, ListChecks, Edit, DollarSign, Clock, Settings, Users, MessageSquare, CheckCircle } from 'lucide-react'; // DODANO CheckCircle

const statusTranslations = {
    pending: 'Oczekujące', auto_quoted: 'Wycenione auto.', manual_quote_required: 'Wycena ręczna',
    quoted: 'Wycenione', accepted: 'Zaakceptowane', rejected: 'Odrzucone',
    in_progress: 'W realizacji', completed: 'Zakończone', cancelled: 'Anulowane',
};
const getStatusClass = (status) => `status-${status?.toLowerCase().replace(/\s+/g, '-').replace(/_/, '-') || 'unknown'}`;
const ITEMS_PER_PAGE = 10;
const ALL_STATUSES = Object.keys(statusTranslations);

const StatCard = ({ title, value, icon, color = 'var(--color-accent)', linkTo }) => {
    const cardContent = (
        <div className="stat-card" style={{border: `1px solid ${color}`, padding: '1.5rem', borderRadius: 'var(--border-radius)', backgroundColor: 'var(--color-bg)', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
            <div style={{color: color, marginBottom: '0.75rem'}}>{icon}</div>
            <h3 style={{fontSize: '1.8rem', margin: '0 0 0.5rem 0', color: 'var(--color-text-headings)'}}>{value}</h3>
            <p style={{fontSize: '0.9rem', color: 'var(--color-text-light)', margin: 0}}>{title}</p>
        </div>
    );
    return linkTo ? <Link to={linkTo} style={{textDecoration: 'none', color: 'inherit'}}>{cardContent}</Link> : cardContent;
};


function AdminDashboardPage() {
    const [quotes, setQuotes] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const currentSortBy = searchParams.get('sortBy') || 'created_at';
    const currentOrder = searchParams.get('order') || 'desc';
    const currentStatusFilter = searchParams.get('statusFilter') || '';
    const currentSearchQuery = searchParams.get('searchQuery') || '';
    const [totalPages, setTotalPages] = useState(1);
    const [totalQuotesInList, setTotalQuotesInList] = useState(0);
    const [searchInput, setSearchInput] = useState(currentSearchQuery);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('adminToken');

    const fetchDashboardData = useCallback(async (page, sortBy, order, statusFilter, searchQuery) => {
        setIsLoading(true); setIsLoadingStats(true); setError('');
        if (!token) { setError('Brak autoryzacji.'); setIsLoading(false); setIsLoadingStats(false); navigate('/admin/login'); return; }
        
        try {
            const params = { page, limit: ITEMS_PER_PAGE, sortBy, order };
            if (statusFilter) { params.statusFilter = statusFilter; }
            if (searchQuery) { params.searchQuery = searchQuery; }

            const [quotesRes, statsRes] = await Promise.all([
                axios.get(`${apiUrl}/api/admin/quotes`, { headers: { 'x-auth-token': token }, params }),
                axios.get(`${apiUrl}/api/admin/dashboard/stats`, { headers: { 'x-auth-token': token } })
            ]);
            
            setQuotes(quotesRes.data.quotes);
            setTotalPages(quotesRes.data.totalPages);
            setTotalQuotesInList(quotesRes.data.totalQuotes);
            setDashboardStats(statsRes.data);

            const newSearchParams = new URLSearchParams();
            newSearchParams.set('page', page.toString());
            if (sortBy !== 'created_at' || (sortBy === 'created_at' && order !== 'desc')) newSearchParams.set('sortBy', sortBy);
            if (order !== 'desc') newSearchParams.set('order', order);
            if (statusFilter) newSearchParams.set('statusFilter', statusFilter);
            if (searchQuery) newSearchParams.set('searchQuery', searchQuery);
            setSearchParams(newSearchParams, { replace: true });

        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się pobrać danych.');
            if (err.response?.status === 401 || err.response?.status === 403) { localStorage.removeItem('adminToken'); navigate('/admin/login'); }
        } finally {
            setIsLoading(false); setIsLoadingStats(false);
        }
    }, [apiUrl, navigate, setSearchParams, token]);

    useEffect(() => {
        fetchDashboardData(currentPage, currentSortBy, currentOrder, currentStatusFilter, currentSearchQuery);
    }, [fetchDashboardData, currentPage, currentSortBy, currentOrder, currentStatusFilter, currentSearchQuery]);

    const handleLogout = () => { localStorage.removeItem('adminToken'); localStorage.removeItem('adminUser'); navigate('/admin/login'); };
    const handlePageChange = (newPage) => { if (newPage >= 1 && newPage <= totalPages) { fetchDashboardData(newPage, currentSortBy, currentOrder, currentStatusFilter, currentSearchQuery); } };
    const handleSort = (columnKey) => { const newOrder = (currentSortBy === columnKey && currentOrder === 'asc') ? 'desc' : 'asc'; fetchDashboardData(1, columnKey, newOrder, currentStatusFilter, currentSearchQuery); };
    const handleStatusFilterChange = (e) => { fetchDashboardData(1, currentSortBy, currentOrder, e.target.value, currentSearchQuery); };
    const handleSearchInputChange = (e) => { setSearchInput(e.target.value); };
    const handleSearchSubmit = (e) => { e.preventDefault(); fetchDashboardData(1, currentSortBy, currentOrder, currentStatusFilter, searchInput.trim()); };
    
    const SortableHeader = ({ columnKey, label }) => ( <th onClick={() => handleSort(columnKey)} style={{ cursor: 'pointer' }}> {label} {currentSortBy === columnKey && (currentOrder === 'asc' ? '▲' : '▼')} </th> );

    return (
        <section className="admin-dashboard-page section-bg-white" style={{ padding: "40px 0", minHeight: 'calc(100vh - var(--header-height) - var(--footer-height, 150px))' }}>
            <div className="container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <h1>Panel Administratora</h1>
                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                        <Link to="/admin/clients" className="button-secondary button-small-outline"><Users size={16} style={{marginRight: '0.3rem'}}/>Klienci</Link> {/* NOWY LINK */}
                        <Link to="/admin/languages" className="button-secondary button-small-outline"><Settings size={16} style={{marginRight: '0.3rem'}}/>Języki</Link>
                        <Link to="/admin/rates" className="button-secondary button-small-outline"><DollarSign size={16} style={{marginRight: '0.3rem'}}/>Stawki</Link>
                        <button onClick={handleLogout} className="button-secondary">Wyloguj</button>
                    </div>
                </div>

                {isLoadingStats && <p>Ładowanie statystyk...</p>}
                {!isLoadingStats && dashboardStats && (
                    <div className="stats-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem'}}>
                        <StatCard title="Wszystkich Zapytań" value={dashboardStats.totalQuotes} icon={<ListChecks size={32}/>} />
                        <StatCard title="Oczekujących" value={dashboardStats.quotesByStatus?.pending || 0} icon={<Clock size={32}/>} color="#ffc107"/>
                        <StatCard title="W Realizacji" value={dashboardStats.quotesByStatus?.in_progress || 0} icon={<Edit size={32}/>} color="#17a2b8"/>
                        <StatCard title="Zakończonych" value={dashboardStats.quotesByStatus?.completed || 0} icon={<CheckCircle size={32}/>} color="#28a745"/>
                        <StatCard title="Wartość Zaakcept." value={`${dashboardStats.totalAcceptedValue} PLN`} icon={<DollarSign size={32}/>} color="#007bff"/>
                        <StatCard title="Zapytań (ost. 7 dni)" value={dashboardStats.recentQuotes} icon={<BarChart2 size={32}/>} />
                    </div>
                )}
                {!isLoadingStats && !dashboardStats && error && <div className="submit-message error">Nie udało się załadować statystyk.</div>}


                <h2>Zapytania o Wycenę ({totalQuotesInList})</h2>
                <div className="admin-controls-wrapper" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
                    <form onSubmit={handleSearchSubmit} className="admin-search-form" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexGrow: 1, minWidth: '250px' }}>
                        <div className="form-group" style={{ marginBottom: 0, flexGrow: 1 }}>
                            <label htmlFor="searchQueryInput" className="sr-only">Szukaj</label>
                            <input type="search" id="searchQueryInput" value={searchInput} onChange={handleSearchInputChange} placeholder="Szukaj po ID, email, nazwie..." style={{padding: '0.5rem 0.75rem', borderRadius: 'var(--border-radius)', border: '1px solid var(--color-border)', width: '100%'}}/>
                        </div>
                        <button type="submit" className="button-primary button-small-icon"><Search size={18}/></button>
                    </form>
                    <div className="admin-filters" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <label htmlFor="statusFilter" style={{ fontWeight: 500 }}><Filter size={16}/> Status:</label>
                        <select id="statusFilter" value={currentStatusFilter} onChange={handleStatusFilterChange} style={{padding: '0.5rem', borderRadius: 'var(--border-radius)', border: '1px solid var(--color-border)'}}>
                            <option value="">Wszystkie</option>
                            {ALL_STATUSES.map(statusKey => ( <option key={statusKey} value={statusKey}>{statusTranslations[statusKey] || statusKey}</option> ))}
                        </select>
                    </div>
                </div>
                
                {(error && quotes.length > 0) && <div className="submit-message error" style={{ marginBottom: '1rem'}}>{error}</div>}
                {isLoading && quotes.length === 0 && <p>Ładowanie listy wycen...</p>}
                {!isLoading && quotes.length === 0 && !error && <p>Brak zapytań o wycenę spełniających kryteria.</p>}

                {quotes.length > 0 && (
                    <div className="table-responsive-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <SortableHeader columnKey="id" label="ID" /> <SortableHeader columnKey="client_name" label="Klient" />
                                    <SortableHeader columnKey="email" label="Email" /> <th>Usługa</th> <th>Języki</th>
                                    <SortableHeader columnKey="status" label="Status" /> <SortableHeader columnKey="created_at" label="Data Złożenia" />
                                    <th>Pliki</th> <th>Akcje</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotes.map(quote => (
                                    <tr key={quote.id} className={quote.status === 'completed' || quote.status === 'cancelled' || quote.status === 'rejected' ? 'quote-row-dimmed' : ''}>
                                        <td>{quote.id}</td><td>{quote.client_name || '-'}</td><td>{quote.email}</td>
                                        <td>{quote.service_type}</td><td>{quote.source_language?.toUpperCase()} → {quote.target_language?.toUpperCase()}</td>
                                        <td><span className={`status-badge ${getStatusClass(quote.status)}`}>{statusTranslations[quote.status] || quote.status}</span></td>
                                        <td>{new Date(quote.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                        <td>{quote.files_count}</td>
                                        <td><Link to={`/admin/quotes/${quote.id}`} className="button-small-outline">Szczegóły</Link></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="pagination-controls" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="button-small-outline"> <ChevronLeft size={18} /> Poprzednia </button>
                        <span>Strona {currentPage} z {totalPages}</span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className="button-small-outline"> Następna <ChevronRight size={18} /> </button>
                    </div>
                )}
            </div>
        </section>
    );
}
export default AdminDashboardPage;